import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { DragIndicator as DragIcon, DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface SortableModelItem {
  id: string;
  name: string;
}

interface SortableRowProps {
  item: SortableModelItem;
  index?: number;
  onSelect?: (item: SortableModelItem) => void;
  onDelete?: (id: string) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({ item, index, onSelect, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.25,
        py: 1,
        mb: 0.75,
        borderRadius: 2,
        border: '1px solid #e2e8f0',
        bgcolor: '#fff',
        boxShadow: isDragging ? '0 4px 12px rgba(15,23,42,0.12)' : 'none',
      }}
    >
      <IconButton size="small" sx={{ cursor: 'grab', color: '#94a3b8' }} {...attributes} {...listeners}>
        <DragIcon fontSize="small" />
      </IconButton>
      <Typography
        onClick={() => onSelect?.(item)}
        sx={{
          flex: 1,
          fontWeight: 600,
          fontSize: '0.875rem',
          color: '#334155',
          cursor: onSelect ? 'pointer' : 'default',
          '&:hover': onSelect ? { color: '#0284c7' } : undefined,
        }}
      >
        {index != null ? `${index + 1}. ${item.name}` : item.name}
      </Typography>
      {onDelete && (
        <IconButton size="small" color="error" onClick={() => onDelete(item.id)}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export interface SortableModelListProps {
  items: SortableModelItem[];
  /** Recebe a nova ordem de ids após arrastar. */
  onReorder: (orderedIds: string[]) => void;
  onSelect?: (item: SortableModelItem) => void;
  onDelete?: (id: string) => void;
  showIndex?: boolean;
}

/** Lista de "Meus modelos" reordenável por arrastar-e-soltar (sugestão médica 29.05). */
const SortableModelList: React.FC<SortableModelListProps> = ({ items, onReorder, onSelect, onDelete, showIndex }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(items, oldIndex, newIndex);
    onReorder(next.map((i) => i.id));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        {items.map((item, index) => (
          <SortableRow
            key={item.id}
            item={item}
            index={showIndex ? index : undefined}
            onSelect={onSelect}
            onDelete={onDelete}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default SortableModelList;
